import { type FormEvent, useCallback, useEffect, useState } from 'react';
import type { MfaEnrollStart } from '@lizrd/contracts';
import * as api from '../../lib/api';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { Stepper, type Step } from '../ui/stepper';
import { RecoveryCodesPanel } from './RecoveryCodesPanel';

type Stage = 'setup' | 'codes';

/**
 * The authenticator-app enrollment flow (Feature 25): scan the QR (or enter the
 * key), confirm a code, then save the one-time recovery codes. Wrapper-agnostic
 * so it powers both the optional Settings dialog and the mandatory full-screen
 * enforcement gate (Phase B).
 *
 * - `onActivated` fires the moment a factor becomes active (optional; the
 *   Settings card uses it to refresh its status behind the dialog).
 * - `onDone` fires when the user finishes after saving recovery codes.
 */
export function MfaEnrollFlow({
  onDone,
  onActivated,
  doneLabel = 'Done',
}: {
  onDone: () => void;
  onActivated?: () => void;
  doneLabel?: string;
}) {
  const [enrollment, setEnrollment] = useState<MfaEnrollStart | null>(null);
  const [stage, setStage] = useState<Stage>('setup');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const begin = useCallback(async () => {
    setError(null);
    setLoadFailed(false);
    try {
      setEnrollment(await api.startMfaEnrollment());
    } catch (err) {
      setLoadFailed(true);
      setError(err instanceof Error ? err.message : 'Could not start setup');
    }
  }, []);

  useEffect(() => {
    void begin();
  }, [begin]);

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    if (!enrollment) return;
    setError(null);
    setBusy(true);
    try {
      const result = await api.activateMfaEnrollment(enrollment.factorId, code.trim());
      setRecoveryCodes(result.recoveryCodes);
      setStage('codes');
      onActivated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code is incorrect');
    } finally {
      setBusy(false);
    }
  }

  // Two real screens (scan+confirm, then recovery codes) → one active step at a
  // time. `spin={false}` so the current step is a calm highlight, not a spinner.
  const steps: Step[] = [
    {
      label: 'Scan & enter code',
      description: 'Scan the QR and confirm the 6-digit code',
      state: stage === 'codes' ? 'done' : 'active',
    },
    { label: 'Save recovery codes', state: stage === 'codes' ? 'active' : 'pending' },
  ];

  return (
    <div className="grid gap-6 sm:grid-cols-[200px_1fr]">
      <Stepper steps={steps} spin={false} className="hidden sm:block" />

      <div className="min-w-0">
        {stage === 'setup' && (
          <div className="space-y-4">
            <p className="text-sm text-haze">
              Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy,
              Microsoft Authenticator), then enter the 6-digit code it shows.
            </p>

            {loadFailed ? (
              <div className="space-y-3">
                <Alert variant="danger">{error ?? 'Could not start setup.'}</Alert>
                <Button variant="outline" size="sm" onClick={() => void begin()}>
                  Try again
                </Button>
              </div>
            ) : !enrollment ? (
              <div className="flex h-48 items-center justify-center">
                <Spinner label="Preparing…" />
              </div>
            ) : (
              <>
                <div className="flex flex-col items-center gap-3 rounded-md border border-hairline bg-surface-2 p-4">
                  <img
                    src={enrollment.qrDataUrl}
                    alt="Authenticator QR code"
                    width={200}
                    height={200}
                    className="rounded-sm bg-white p-2"
                  />
                  <p className="text-center text-xs text-muted">
                    Can&rsquo;t scan? Enter this key manually:
                    <br />
                    <span className="mt-1 inline-block break-all font-mono text-xs text-haze">
                      {enrollment.secret}
                    </span>
                  </p>
                </div>

                <form onSubmit={onVerify} className="space-y-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
                      6-digit code
                    </span>
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      className="text-center font-mono text-lg tracking-[0.4em]"
                    />
                  </label>
                  {error && <p className="text-sm text-danger">{error}</p>}
                  <Button type="submit" disabled={busy || !code.trim()} className="w-full">
                    {busy ? 'Verifying…' : 'Verify & turn on'}
                  </Button>
                </form>
              </>
            )}
          </div>
        )}

        {stage === 'codes' && (
          <div className="space-y-4">
            <RecoveryCodesPanel codes={recoveryCodes} />
            <Button onClick={onDone} className="w-full">
              {doneLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
